import type { ReactNode } from 'react';
import { Check, Clock, User, Shirt, Utensils, Wrench, MapPin, KeyRound, ParkingCircle, Info } from 'lucide-react';
import { MEAL_LABELS, PHASE_LABELS, TIPS_LABELS, clockAt, durationLabel, type BriefingView as Briefing, type Phase } from '../briefing.ts';

const hasPractical = (b: Briefing) =>
  Boolean(b.dressCode || b.equipmentNotes || b.parkingInfo || b.notes || b.meal !== 'NONE' || b.breakMinutes || b.tips !== 'UNKNOWN' || b.briefingMinutesBefore != null);

function Row({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
      <span style={{ color: 'var(--text-tertiary)', marginTop: 2 }} aria-hidden>{icon}</span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase' }}>{label}</div>
        <div style={{ fontSize: 14, lineHeight: 1.5, overflowWrap: 'anywhere' }}>{children}</div>
      </div>
    </div>
  );
}

/**
 * Fiche pratique d'une mission. `audience` : l'extra peut cocher la check-list (quand la mission est confirmée),
 * l'établissement voit qui a coché quoi. Les informations sensibles n'arrivent que si le serveur les a autorisées.
 */
export function BriefingView({ briefing: b, startsAt, audience, canCheck, onCheck }: {
  briefing: Briefing;
  startsAt: string;
  audience: 'extra' | 'org';
  canCheck?: boolean;
  onCheck?: (itemId: string, checked: boolean) => void;
}) {
  const priv = b.private;
  const empty = !hasPractical(b) && b.schedule.length === 0 && b.checklist.length === 0 && !priv;
  if (empty) return null;

  const phases = (['BEFORE', 'DURING', 'AFTER'] as Phase[]).map((p) => [p, b.checklist.filter((c) => c.phase === p)] as const).filter(([, items]) => items.length > 0);
  const done = b.checklist.filter((c) => (audience === 'extra' ? c.checked : (c.checks?.length ?? 0) > 0)).length;

  return (
    <>
      {hasPractical(b) && (
        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontWeight: 800 }}>Infos pratiques</div>
          {b.dressCode && <Row icon={<Shirt size={16} />} label="Tenue">{b.dressCode}</Row>}
          {b.equipmentNotes && <Row icon={<Wrench size={16} />} label="Matériel">{b.equipmentNotes}</Row>}
          {b.parkingInfo && <Row icon={<ParkingCircle size={16} />} label="Parking et transports">{b.parkingInfo}</Row>}
          {b.briefingMinutesBefore != null && (
            <Row icon={<Clock size={16} />} label="Brief avant le service">À {clockAt(startsAt, -b.briefingMinutesBefore)} ({b.briefingMinutesBefore} min avant le début)</Row>
          )}
          {(b.meal !== 'NONE' || b.breakMinutes > 0 || b.tips !== 'UNKNOWN') && (
            <Row icon={<Utensils size={16} />} label="Repas, pause, pourboires">
              {[
                MEAL_LABELS[b.meal],
                b.breakMinutes ? `Pause de ${b.breakMinutes} min${b.breakPaid ? ' (rémunérée)' : ' (non rémunérée)'}` : null,
                b.tips !== 'UNKNOWN' ? TIPS_LABELS[b.tips] : null,
              ].filter(Boolean).join(' · ')}
            </Row>
          )}
          {b.notes && <Row icon={<Info size={16} />} label="Informations diverses"><span style={{ whiteSpace: 'pre-wrap' }}>{b.notes}</span></Row>}
        </div>
      )}

      {b.schedule.length > 0 && (
        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontWeight: 800 }}>Planning de la prestation</div>
          {b.schedule.map((s) => (
            <div key={s.id} style={{ display: 'flex', gap: 12, alignItems: 'baseline' }}>
              <span style={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums', minWidth: 48 }}>{clockAt(startsAt, s.offsetMin)}</span>
              <span style={{ flex: 1, fontSize: 14 }}>{s.label}</span>
              {s.durationMin && <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{durationLabel(s.durationMin)}</span>}
            </div>
          ))}
        </div>
      )}

      {b.checklist.length > 0 && (
        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <div style={{ fontWeight: 800 }}>Check-list</div>
            {(audience === 'org' || canCheck) && <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{done}/{b.checklist.length}</span>}
          </div>
          {audience === 'extra' && !canCheck && (
            <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>Vous pourrez cocher ces points sur place, une fois la mission confirmée.</div>
          )}
          {phases.map(([phase, items]) => (
            <div key={phase} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase' }}>{PHASE_LABELS[phase]}</div>
              {items.map((c) => (
                <label key={c.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 14, cursor: canCheck ? 'pointer' : 'default' }}>
                  {audience === 'extra' ? (
                    <input type="checkbox" checked={Boolean(c.checked)} disabled={!canCheck} onChange={(e) => onCheck?.(c.id, e.target.checked)} style={{ marginTop: 3 }} />
                  ) : (
                    <span aria-hidden style={{ width: 16, color: (c.checks?.length ?? 0) > 0 ? 'var(--success)' : 'var(--text-tertiary)', marginTop: 1 }}>
                      {(c.checks?.length ?? 0) > 0 ? <Check size={16} /> : '○'}
                    </span>
                  )}
                  <span style={{ flex: 1 }}>
                    {c.label}
                    {c.required && <span style={{ color: 'var(--danger)', fontSize: 12, marginLeft: 6 }}>obligatoire</span>}
                    {audience === 'org' && (c.checks?.length ?? 0) > 0 && (
                      <span style={{ display: 'block', fontSize: 12, color: 'var(--text-tertiary)' }}>
                        {c.checks!.map((k) => `${k.firstName}, ${new Date(k.checkedAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`).join(' · ')}
                      </span>
                    )}
                  </span>
                </label>
              ))}
            </div>
          ))}
        </div>
      )}

      {priv && (priv.arrivalInstructions || priv.accessCode || priv.contactName || audience === 'extra') && (
        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 12, borderColor: 'var(--accent)' }}>
          <div style={{ fontWeight: 800 }}>Accès et contacts</div>
          <Row icon={<MapPin size={16} />} label="Adresse"><a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(priv.address)}`} target="_blank" rel="noreferrer" style={{ color: 'var(--accent-dark)', fontWeight: 700 }}>{priv.address}</a></Row>
          {priv.arrivalInstructions && <Row icon={<Info size={16} />} label="Consignes d’arrivée">{priv.arrivalInstructions}</Row>}
          {priv.accessCode && <Row icon={<KeyRound size={16} />} label="Code d’accès"><strong style={{ letterSpacing: 1 }}>{priv.accessCode}</strong></Row>}
          {priv.contactName && <Row icon={<User size={16} />} label="Personne à demander sur place">{priv.contactName}</Row>}
        </div>
      )}
    </>
  );
}
